/** Клиент раздела «Себестоимость» (до 22.10.2026 — «Закупочные цены»; `/api/purchase-prices/*`, backend `purchase_prices/`, право
 *  `purchase_prices`). Схемы — буквально из `router.py` / `service.py` (`price_view`, `rate_view`,
 *  `_fixation_view`, `build_preview`). Источник правды — `docs/purchase-prices.md`.
 *
 *  Деньги — числа JSON с 2 знаками; цена хранится С НДС (`price_gross`), `cost_net` — расчёт
 *  производства без НДС (только у `production`, у исправления — null). */
import { readBlobError } from "@/lib/api-errors";
import { api } from "./client";

const B = "/purchase-prices";

export type PriceSource = "production" | "contract" | "manual";
export type HumanSource = Exclude<PriceSource, "production">;

/** `service.price_view` */
export interface PriceRow {
  id: number;
  nomenclature_ref: string;
  characteristic_ref: string;
  spec_ref: string | null;
  article: string | null;
  name: string | null;
  /** название характеристики 1С; null — не задано (ручная цена без подписи, строки до 0053) */
  characteristic_name: string | null;
  valid_from: string;
  source: PriceSource;
  cost_net: number | null;
  vat_rate: number;
  price_gross: number;
  fixation_id: number | null;
  replaces_id: number | null;
  replaced_by_id: number | null;
  reason: string | null;
  created_by: string;
  created_at: string | null;
}

/** `service.rate_view` */
export interface WarehouseRate {
  id: number;
  valid_from: string;
  rub_per_unit: number;
  note: string | null;
  created_by: string;
  created_at: string | null;
}

export interface PricesList {
  date: string;
  items: (PriceRow & { history_count: number })[];
  warehouse_rate: WarehouseRate | null;
}

export interface PriceHistory {
  nomenclature_ref: string;
  characteristic_ref: string;
  items: PriceRow[];
}

// ── фиксации ─────────────────────────────────────────────────────────────────
export type FixationKind = "quarter" | "new_items";
export type FixationStatus = "running" | "preview" | "failed" | "confirmed" | "discarded";
/** `flags.py`, приоритет: kept_manual > exists_same_date > not_computed > multi_spec > warning >
 *  jump > ok. Старый флаг `issue` (до 21.10.2026) бэкенд поднимает на чтении только у фиксаций в
 *  статусе `preview`; у подтверждённых/отброшенных/неудавшихся (история) он приходит как сохранён. */
export type PreviewFlag =
  | "ok"
  | "jump"
  /** пишущаяся спецификация посчитана, но с предупреждением — пишется, если не снять галочку */
  | "warning"
  /** ни одной посчитанной спецификации — не пишется */
  | "not_computed"
  /** посчитанных спецификаций две и больше — пишется только с выбором рецептуры */
  | "multi_spec"
  | "kept_manual"
  | "exists_same_date"
  /** квартал: действует цена производства, а в расчёте характеристики нет — не пишется */
  | "missing_in_calc"
  /** легаси (до 21.10.2026): «не посчитано» и «с замечаниями» вместе; только в истории */
  | "issue";

/** `spec_choice` для спецификации без `spec_ref` (`service.SPEC_NONE`). */
export const SPEC_NONE = "__none__";

export interface PreviewSpec {
  spec_ref: string | null;
  spec_name: string | null;
  cost_net_rub: number | null;
  /** round(round(cost_net_rub, 2) × 1,22, 2); null — себестоимости нет / не положительная */
  price_gross: number | null;
  material_source: string | null;
  /** все замечания производства; у непосчитанной (`ok=false`) — причины */
  issues: string[];
  /** замечания посчитанной, требующие взгляда (всё, что не допущение); у `ok=false` — пусто */
  warnings: string[];
  /** допущения методики (`flags.ASSUMPTION_PATTERNS`), справочно; у `ok=false` — пусто */
  assumptions: string[];
  ok: boolean;
}

export interface PreviewRow {
  key: string;
  nomenclature_ref: string;
  characteristic_ref: string;
  article: string | null;
  name: string;
  /** нет ключа у предпросмотров, сохранённых до 0053 — подпись просто не показывается */
  characteristic_name?: string | null;
  kind: string | null;
  /** пусто у `missing_in_calc` */
  specs: PreviewSpec[];
  current: { price_gross: number; source: PriceSource; valid_from: string } | null;
  /** только у строк с одной посчитанной спецификацией и действующей ценой */
  jump_pct: number | null;
  /** значение выбора (`spec_ref` или `__none__`) спецификации, которая пишется без выбора человека:
   *  единственная посчитанная или несколько с равной ценой (первая); null — нет / нужен выбор */
  auto_spec: string | null;
  flag: PreviewFlag;
}

export interface FixationPreview {
  rates_version_id: number | null;
  rates_created_at: string | null;
  computed_at: string | null;
  assumptions: string[];
  jump_threshold_pct: number;
  vat_rate: number;
  counts: Partial<Record<PreviewFlag, number>>;
  rows: PreviewRow[];
}

export interface ConfirmResult {
  written: number;
  skipped: { key: string; reason: string }[];
}

/** `_fixation_view(with_rows=False)` — строка списка */
export interface FixationSummary {
  id: number;
  kind: FixationKind;
  calc_date: string;
  valid_from: string;
  qty: number;
  manuf_job_id: number | null;
  status: FixationStatus;
  error: string | null;
  created_by: string;
  created_at: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  counts: Partial<Record<PreviewFlag, number>> | null;
  confirm_result: ConfirmResult | null;
  progress: { done: number; total: number } | null;
}

/** `_fixation_view(with_rows=True)` — `GET /fixations/{id}` */
export interface Fixation extends FixationSummary {
  preview: FixationPreview | null;
}

export interface FixationIn {
  kind: FixationKind;
  calc_date: string;
  valid_from: string;
  qty: number;
}

export interface ConfirmIn {
  exclude: string[];
  /** ключ → spec_ref; спецификация без spec_ref — `SPEC_NONE` */
  spec_choice: Record<string, string>;
}

// ── без цены (`GET /missing`) ────────────────────────────────────────────────
/** `no_offer` — продажа по технической карточке маркетплейса: оффера и ссылок 1С нет */
export type MissingStatus = "no_price" | "unmapped" | "ambiguous" | "no_offer";

export interface MissingOffer {
  mp: string;
  mp_sku: string;
  vendor_code: string | null;
  name: string | null;
}

export interface MissingItem {
  status: MissingStatus;
  nomenclature_ref: string | null;
  characteristic_ref: string | null;
  code_1c: string | null;
  article_1c: string | null;
  name_1c: string | null;
  /** имя характеристики 1С из витрины (MIN по строкам); null — «без характеристики» или имя в 1С
   *  не указано (различаются по `characteristic_ref`: нулевой GUID — без характеристики) */
  characteristic_name: string | null;
  offers: MissingOffer[];
  qty: number;
  revenue: number;
  /** цена портала, уже действующая на `cogs_from` (DWH отстаёт — учтёт после загрузки); null — нет */
  portal_price: { price_gross: number; valid_from: string; source: string } | null;
  /** где продано без цены (с 22.10.2026): МП, опт или оба — одна строка на характеристику */
  sources: MissingSource[];
  /** разбивка по каналам; `qty` / `revenue` товара — продано: МП + опт `shipped` (остатки заказов
   *  опта — только в своих бакетах, виды опта не складываются) */
  by_source: {
    mp: { qty: number; revenue: number } | null;
    opt: Record<"shipped" | "planned" | "overdue", MissingOptBucket | null> | null;
  };
  /** ближайшая отгрузка опта без цены (с 22.10.2026): дата, суммарные штуки к отгрузке и готовый
   *  текст «внесите цену до отгрузки» (тот же — в xlsx) */
  planned_next: { date: string; qty: number; comment: string } | null;
}

export type MissingSource = "mp" | "opt";
/** Фильтр «МП / Опт / Все» (`source` запроса). */
export type MissingSourceFilter = "all" | "mp" | "opt";

/** Бакет вида строк опта без цены: `revenue` null — ни у одной строки нет суммы (остаток заказа
 *  без строки заказа), `revenue_unknown_rows` — сколько таких строк. Штуки бывают дробными. */
export interface MissingOptBucket {
  rows: number;
  qty: number;
  revenue: number | null;
  revenue_unknown_rows: number;
}

/** Оптовая часть «Без цены»: `ok`; `not_requested` (source = mp); `not_applicable` (фильтр кабинета /
 *  площадки — только МП); `forbidden` (нет права «Аналитика ОПТ»); `error` — витрина опта недоступна
 *  или собрана частично (`message`), МП-часть показана. */
export interface MissingOpt {
  status: "ok" | "not_requested" | "not_applicable" | "forbidden" | "error";
  message: string | null;
  state: { built_at: string; build_id: string; orders_as_of: string; sales_date_to: string | null } | null;
  /** отгрузки опта без цены раньше `cogs_from` — законно без цены, в список не вошли */
  before_cogs_from: { rows: number; qty: number; revenue: number } | null;
  /** всегда 0 (поле для совместимости): плановые остатки заказов теперь в списке независимо от «по» */
  open_after_period: { rows: number; qty: number } | null;
}

/** Товар 1С из поиска «Добавить цену» (`GET /products?q=`): проданный с `cogs_from`. */
export interface ProductItem {
  nomenclature_ref: string;
  /** «без характеристики» — нулевой GUID */
  characteristic_ref: string;
  code_1c: string | null;
  article_1c: string | null;
  name_1c: string | null;
  characteristic_name: string | null;
  offers: MissingOffer[];
  /** у характеристики есть цена портала, действующая на `cogs_from` */
  has_price: boolean;
  /** её `valid_from` (YYYY-MM-DD); null — цены нет */
  price_valid_from: string | null;
}

export interface ProductSearch {
  cogs_from: string;
  items: ProductItem[];
  /** найдено больше 30 — показаны первые (выручка ↓), нужно уточнить запрос */
  truncated: boolean;
}

/** Свежесть DWH (`mart.mp_cogs_state`): `built_at` — сборка себестоимости, `prices_exported_at` — снимок
 *  цен портала, который взял DWH (null — цен ещё не брал). ISO-время. */
export interface MissingDwh {
  built_at: string | null;
  prices_exported_at: string | null;
}

export interface MissingList {
  cogs_from: string | null;
  dwh: MissingDwh;
  /** последнее изменение цен/ставок склада в портале (max `created_at`); null — не было */
  portal_changed_at: string | null;
  /** портал менялся после снимка DWH — список и маржа ещё на старых ценах */
  stale: boolean;
  items: MissingItem[];
  source: MissingSourceFilter;
  opt: MissingOpt;
}

export interface PriceIn {
  nomenclature_ref: string;
  characteristic_ref: string;
  article: string | null;
  name: string | null;
  /** имя характеристики 1С (подпись строки); null — нет */
  characteristic_name: string | null;
  valid_from: string;
  /** строкой «1234.56» — без потерь плавающей точки (бэкенд — Decimal) */
  price_gross: string;
  source: HumanSource;
  reason: string;
}

/** 422 загрузки xlsx: `{detail: {message, errors}}` */
export interface XlsxRowError {
  row: number;
  column: string | null;
  message: string;
}

// ── вызовы ───────────────────────────────────────────────────────────────────
export const getPrices = async (date: string, q: string): Promise<PricesList> =>
  (await api.get<PricesList>(`${B}/prices`, { params: { date, ...(q ? { q } : {}) } })).data;

export const getPriceHistory = async (nom: string, char: string): Promise<PriceHistory> =>
  (
    await api.get<PriceHistory>(
      `${B}/prices/${encodeURIComponent(nom)}/${encodeURIComponent(char)}/history`,
    )
  ).data;

export const addPrice = async (body: PriceIn): Promise<PriceRow> =>
  (await api.post<PriceRow>(`${B}/prices`, body)).data;

export const correctPrice = async (
  id: number,
  body: { price_gross: string; reason: string },
): Promise<PriceRow> => (await api.post<PriceRow>(`${B}/prices/${id}/correct`, body)).data;

export const getWarehouseRates = async (): Promise<WarehouseRate[]> =>
  (await api.get<{ items: WarehouseRate[] }>(`${B}/warehouse-rates`)).data.items;

export const addWarehouseRate = async (body: {
  valid_from: string;
  rub_per_unit: string;
  note: string | null;
}): Promise<WarehouseRate> => (await api.post<WarehouseRate>(`${B}/warehouse-rates`, body)).data;

export const listFixations = async (): Promise<FixationSummary[]> =>
  (await api.get<{ items: FixationSummary[] }>(`${B}/fixations`, { params: { limit: 50 } })).data
    .items;

export const getFixation = async (id: number): Promise<Fixation> =>
  (await api.get<Fixation>(`${B}/fixations/${id}`)).data;

export const createFixation = async (body: FixationIn): Promise<{ id: number }> =>
  (await api.post<{ id: number }>(`${B}/fixations`, body)).data;

export const confirmFixation = async (id: number, body: ConfirmIn): Promise<ConfirmResult> =>
  (await api.post<ConfirmResult>(`${B}/fixations/${id}/confirm`, body)).data;

export const discardFixation = async (id: number): Promise<FixationSummary> =>
  (await api.post<FixationSummary>(`${B}/fixations/${id}/discard`)).data;

export const getMissing = async (
  dateFrom: string,
  dateTo: string,
  legal: string | null = null,
  mp: string | null = null,
  source: MissingSourceFilter = "all",
): Promise<MissingList> =>
  (
    await api.get<MissingList>(`${B}/missing`, {
      params: { date_from: dateFrom, date_to: dateTo, ...(legal ? { legal } : {}), ...(mp ? { mp } : {}), source },
    })
  ).data;

/** `cogs_from` DWH — «Действует с» по умолчанию в «Добавить цену» из шапки; null — не задана. */
export const getCogsFrom = async (): Promise<string | null> =>
  (await api.get<{ cogs_from: string | null }>(`${B}/cogs-from`)).data.cogs_from;

/** Поиск товара 1С для «Добавить цену» (`q` — от 2 символов). */
export const searchProducts = async (q: string): Promise<ProductSearch> =>
  (await api.get<ProductSearch>(`${B}/products`, { params: { q } })).data;

/** «Без цены» пачкой: шаблон загрузки, заполненный товарами без цены (`GET /missing.xlsx`). */
export const downloadMissingXlsx = async (
  dateFrom: string,
  dateTo: string,
  legal: string | null = null,
  mp: string | null = null,
  source: MissingSourceFilter = "all",
): Promise<Blob> => {
  try {
    return (
      await api.get(`${B}/missing.xlsx`, {
        params: { date_from: dateFrom, date_to: dateTo, ...(legal ? { legal } : {}), ...(mp ? { mp } : {}), source },
        responseType: "blob",
      })
    ).data as Blob;
  } catch (e) {
    throw await readBlobError(e);
  }
};

/** Шаблон xlsx — телом ответа; ошибка-блоб дочитывается, чтобы причина была видна. */
export const downloadTemplate = async (): Promise<Blob> => {
  try {
    return (await api.get(`${B}/prices/template.xlsx`, { responseType: "blob" })).data as Blob;
  } catch (e) {
    throw await readBlobError(e);
  }
};

export const importPricesXlsx = async (file: File): Promise<{ written: number }> => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post<{ written: number }>(`${B}/prices/import.xlsx`, fd)).data;
};
